import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Percent, DollarSign, TrendingUp, Users, Loader2, Filter, CheckCircle2, ExternalLink, Search,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  pending:  { color: "bg-amber-500/15 border-amber-500/30 text-amber-300",   label: "Pending" },
  released: { color: "bg-blue-500/15 border-blue-500/30 text-blue-300",      label: "Released" },
  paid:     { color: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300", label: "Paid" },
  cancelled:{ color: "bg-slate-600/20 border-slate-500/30 text-slate-400",   label: "Cancelled" },
};

export default function SalesCommissions() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get("status") === "needs_review" ? "needs_review" : "all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      // "needs_review" is a client-side filter (backend expects true status values)
      if (filter !== "all" && filter !== "needs_review") params.status = filter;
      if (search.trim()) params.customer = search.trim();
      const [list, sum] = await Promise.all([
        api.get("/sales/commissions", { params }),
        api.get("/sales/commissions/summary"),
      ]);
      setItems(list.data);
      setSummary(sum.data);
    } catch { toast.error("Failed to load commissions"); }
    finally { setLoading(false); }
  };

  const filteredItems = useMemo(() => {
    if (filter === "needs_review") return items.filter((c) => c.needs_review);
    return items;
  }, [items, filter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const payOut = async (id) => {
    if (!window.confirm("Confirm: marking this commission as PAID?")) return;
    try {
      await api.post(`/sales/commissions/${id}/pay`, {});
      toast.success("Marked as paid");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resolveReview = async (id, action) => {
    const label = action === "keep" ? "MANTER" : "ESTORNAR";
    if (!window.confirm(`Confirmar: ${label} esta comissão?\n\n${action === "keep" ? "A comissão continua ativa e será paga normalmente." : "A comissão será cancelada. Se já foi paga, você precisa recuperar o valor separadamente."}`)) return;
    let reason = null;
    if (action === "cancel") {
      reason = window.prompt("Motivo do estorno (opcional):") || null;
    }
    try {
      await api.post(`/sales/commissions/${id}/resolve-review`, { action, reason });
      toast.success(action === "keep" ? "Comissão mantida" : "Comissão estornada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Percent className="w-7 h-7 text-violet-400" /> Sales Commissions
        </h1>
        <p className="text-slate-400 text-sm mt-1">10% on every paid invoice. PENDING → RELEASED when payment arrives → PAID when paid out to salesperson.</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="commissions-summary">
          <KPI label="Total Sold" value={`$${summary.total_sold.toLocaleString()}`} icon={TrendingUp} color="blue" testId="kpi-sold" />
          <KPI label="Received" value={`$${summary.received_by_company.toLocaleString()}`} icon={DollarSign} color="emerald" testId="kpi-received" />
          <KPI label="Pending" value={`$${summary.commission_pending.toLocaleString()}`} icon={Percent} color="amber" testId="kpi-pending" sub={`${summary.counts.pending} invoice(s)`} />
          <KPI label="Released" value={`$${summary.commission_released.toLocaleString()}`} icon={CheckCircle2} color="cyan" testId="kpi-released" sub={`${summary.counts.released} invoice(s)`} />
          <KPI label="Paid" value={`$${summary.commission_paid.toLocaleString()}`} icon={Users} color="emerald" testId="kpi-paid" sub={`${summary.counts.paid} invoice(s)`} />
        </div>
      )}

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-3 flex flex-wrap items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-slate-500" />
        {["all", "pending", "released", "paid", "cancelled", "needs_review"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s}`}
            className={`px-3 py-1 rounded-md text-xs uppercase tracking-wider font-medium border ${filter === s ? (s === "needs_review" ? "border-rose-500/40 bg-rose-500/10 text-rose-300" : "border-violet-500/40 bg-violet-500/10 text-violet-300") : "border-slate-800 text-slate-400 hover:border-slate-600"}`}
          >{s === "needs_review" ? `⚠ Review${summary?.needs_review_count ? ` (${summary.needs_review_count})` : ""}` : s}</button>
        ))}
        <div className="ml-auto relative w-full md:w-64">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer..."
            className="pl-8 bg-[#030712] border-slate-800"
            data-testid="commission-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center p-12"><Loader2 className="w-6 h-6 animate-spin text-violet-400 mx-auto" /></div>
      ) : filteredItems.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-12 text-center" data-testid="commissions-empty">
          <Percent className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-semibold">
            {filter === "needs_review" ? "Nenhuma comissão precisa de revisão" : "No commissions yet"}
          </p>
          <p className="text-slate-500 text-sm mt-1">
            {filter === "needs_review"
              ? "Comissões só aparecem aqui quando uma invoice paga é cancelada depois."
              : "Create an invoice and it'll appear here automatically with 10% pending."}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Customer</th>
                <th className="text-left p-3">Invoice</th>
                <th className="text-right p-3">Invoice $</th>
                <th className="text-right p-3">Rate</th>
                <th className="text-right p-3">Commission</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((c) => {
                const conf = STATUS_CONFIG[c.status] || STATUS_CONFIG.pending;
                return (
                  <tr key={c.id} className="border-b border-slate-800/60 hover:bg-slate-800/20" data-testid={`commission-${c.id}`}>
                    <td className="p-3 text-slate-400 text-xs">{(c.created_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-slate-200">{c.customer_name || "—"}</td>
                    <td className="p-3 text-slate-300 font-mono text-xs">
                      <Link to={`/invoices/${c.invoice_id}`} className="hover:text-blue-300 flex items-center gap-1">
                        {c.invoice_number || c.invoice_id.slice(0, 8)} <ExternalLink className="w-2.5 h-2.5" />
                      </Link>
                    </td>
                    <td className="p-3 text-right text-slate-300 font-mono">${Number(c.invoice_total || 0).toLocaleString()}</td>
                    <td className="p-3 text-right text-slate-400">{(c.rate * 100).toFixed(0)}%</td>
                    <td className="p-3 text-right font-semibold text-violet-300 font-mono">${Number(c.amount).toLocaleString()}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge className={`${conf.color} text-[10px]`}>{conf.label}</Badge>
                        {c.needs_review && (
                          <Badge className="bg-rose-500/15 border-rose-500/40 text-rose-300 text-[10px] font-bold" title={c.review_reason || "Requires manual review"} data-testid={`needs-review-${c.id}`}>
                            ⚠ REVIEW
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      {c.needs_review ? (
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm" onClick={() => resolveReview(c.id, "keep")}
                            className="bg-emerald-600 hover:bg-emerald-500 text-xs h-7 px-2"
                            data-testid={`review-keep-${c.id}`}
                            title="Manter comissão — continuará sendo paga"
                          >
                            ✓ Manter
                          </Button>
                          <Button
                            size="sm" onClick={() => resolveReview(c.id, "cancel")}
                            className="bg-rose-600 hover:bg-rose-500 text-xs h-7 px-2"
                            data-testid={`review-cancel-${c.id}`}
                            title="Estornar mesmo assim"
                          >
                            ✕ Estornar
                          </Button>
                        </div>
                      ) : c.status === "released" && (
                        <Button
                          size="sm" onClick={() => payOut(c.id)}
                          className="bg-emerald-600 hover:bg-emerald-500 text-xs h-7"
                          data-testid={`pay-${c.id}`}
                        >
                          Mark Paid
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, sub, testId }) {
  const c = {
    blue: "border-blue-500/30 text-blue-300",
    amber: "border-amber-500/30 text-amber-300",
    emerald: "border-emerald-500/30 text-emerald-300",
    cyan: "border-cyan-500/30 text-cyan-300",
    violet: "border-violet-500/30 text-violet-300",
  }[color];
  return (
    <div data-testid={testId} className={`rounded-xl border ${c} bg-[#0B1120] p-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <Icon className="w-4 h-4 opacity-60" />
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
